import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { get } from 'lodash';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  CreateAIConversationRequest,
  SubmitMessageRequest,
} from '@store/actions/aiConversationAction';
import {
  createAIChatSession,
  createAIChatSessionFailure,
  createAIChatSessionSuccess,
  sendMessageWithAI,
  sendMessageWithAIFailure,
  sendMessageWithAISuccess,
  fetchCourseList,
  fetchCourseListSuccess,
  fetchCourseListFailure,
  setCurrentCourse,
  getVTeacherUrlFailure,
  getVTeacherUrlRequest,
  getVTeacherUrlSuccess,
  CourseData,
} from '@store/reducers/aiConversationReducer';
import { v4 as uuidv4 } from 'uuid';
import { resourceService } from '@libs/axios';

const API_URL = process.env.REACT_AI_AGENT_API_URL;
const API_URL_CHAT = process.env.REACT_APP_API_URL;

function* createAIConversationSessionSaga(
  action: PayloadAction<CreateAIConversationRequest>,
) {
  try {
    const sessionId = uuidv4();
    const response: { data: { message: string } } = yield call(
      axios.post,
      API_URL + '/api/v2/conversation/lmschat/',
      {
        session_id: sessionId,
        context: action.payload.context || action.payload.content,
        message: action.payload.content,
      },
    );
    yield put(
      createAIChatSessionSuccess({
        session_id: sessionId,
        ...response.data,
      }),
    );
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to create chat session';
    yield put(createAIChatSessionFailure(errorMessage));
  }
}

function* sendMessageWithAISaga(action: PayloadAction<SubmitMessageRequest>) {
  try {
    const response: {
      data: { message: string };
    } = yield call(axios.post, API_URL + '/api/v2/conversation/lmschat/', {
      session_id: action.payload.session_id,
      context: action.payload.context || action.payload.content || '',
      message: action.payload.content || '',
    });

    yield put(
      sendMessageWithAISuccess({
        ai_response: {
          content: get(response, 'data.message', ''),
        },
      }),
    );
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to send message';
    yield put(sendMessageWithAIFailure(errorMessage));
  }
}

function* fetchCourseListSaga() {
  try {
    const response: {
      data: { results: CourseData[] };
    } = yield call(resourceService.get, API_URL_CHAT + '/api/chat_data/', {
      params: {
        page_size: 20
      }
    });
    yield put(fetchCourseListSuccess(response.data.results));
    yield put(setCurrentCourse(response.data.results[0]));
  } catch (error: any) {
    yield put(fetchCourseListFailure(error.message));
  }
}

function* getVTeacherUrlSaga(action: PayloadAction<{ id: number }>) {
  try {
    const response: {
      data: { url: string };
    } = yield call(
      resourceService.get,
      API_URL_CHAT + `/api/chat_data/${action.payload.id}/vteacher_url/`,
    );

    yield put(getVTeacherUrlSuccess({ url: response.data.url }));
  } catch (error: any) {
    yield put(getVTeacherUrlFailure(error.message));
  }
}
function* aiConversationSaga() {
  yield takeLatest(createAIChatSession.type, createAIConversationSessionSaga);
  yield takeLatest(sendMessageWithAI.type, sendMessageWithAISaga);
  yield takeLatest(fetchCourseList.type, fetchCourseListSaga);
  yield takeLatest(getVTeacherUrlRequest.type, getVTeacherUrlSaga);
}

export default aiConversationSaga;
